import { useState } from 'react';
import {
  Box,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material';
import { CarrierServicingGrantInputDto, CourierLoginMethod } from '@groundfloor-platform/mgmt-service-g-sdk';
import { RestError } from '@azure/core-http';
import { useQueryClient } from 'react-query';
import { Modal } from '../../../ui/components/pure/modal/modal.component';
import { TextButton } from '../../../ui/components/pure/text-button/text-button.component';
import { parseErrorMessage } from '../../../core/api/api.context';
import { QueryKeys } from '../../../core/queries/query-keys.constants';
import { useQueryGetProject, useQueryListNetworkProjects } from '../../../core/queries/projects.queries';
import { useMutationCreateProject } from '../../settings/settings.queries';
import { useMutationCreateCarrierOrganisation } from '../carrier-organisations.queries';
import {
  AddCarrierDetails,
  CARRIER_PROJECT_TYPE,
  DEFAULT_LOGIN_METHOD,
  LOGIN_METHOD_OPTIONS,
  EMPTY_DETAILS,
  isDetailsStepComplete,
  isNetworksStepComplete,
  toCreateCarrierOrganisationDto,
  toCreateProjectDto
} from '../logic/add-carrier-form';
import {
  hasPartialProgress,
  isComplete,
  NOT_STARTED,
  RunState,
  StepDefinition,
  viewSteps
} from '../logic/processing-steps';
import { DetailsStep } from './steps/details-step';
import { ProcessingStep } from './steps/processing-step';
import { NetworksStep } from './steps/networks-step';
import { ReviewStep } from './steps/review-step';

const STEPS = ['Details', 'Networks', 'Review'] as const;

const PROCESSING: StepDefinition[] = [
  { id: 'project', label: 'Create the carrier’s project' },
  { id: 'organisation', label: 'Set up the organisation, its networks and its couriers group' }
];

export interface AddCarrierModalProps {
  /** The project the wizard was opened from. The carrier's project becomes a child of it. */
  parentProjectId: string;
  onSuccess: (carrierProjectId: string, carrierName: string) => void;
  onCancel: () => void;
}

export function AddCarrierModal({ parentProjectId, onSuccess, onCancel }: AddCarrierModalProps) {
  const [step, setStep] = useState(0);
  const [details, setDetails] = useState<AddCarrierDetails>(EMPTY_DETAILS);
  const [servicing, setServicing] = useState<CarrierServicingGrantInputDto[]>([]);
  const [showDetailsValidation, setShowDetailsValidation] = useState(false);
  const [shortcodeError, setShortcodeError] = useState<string | undefined>();
  // Held from the moment step 1 succeeds: a retry must finish this project, not create a second.
  // The setup call only refuses a project that already manages an organisation.
  const [carrierProjectId, setCarrierProjectId] = useState<string | undefined>();
  const [loginMethod, setLoginMethod] = useState<CourierLoginMethod>(DEFAULT_LOGIN_METHOD);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [run, setRun] = useState<RunState>(NOT_STARTED);
  const [isProcessing, setIsProcessing] = useState(false);

  const parentProject = useQueryGetProject(parentProjectId);
  const networks = useQueryListNetworkProjects();
  const createProject = useMutationCreateProject();
  const createOrganisation = useMutationCreateCarrierOrganisation();
  const queryClient = useQueryClient();

  const isSubmitting = createProject.isLoading || createOrganisation.isLoading;
  const detailsLocked = !!carrierProjectId;

  const onNext = () => {
    if (step === 0) {
      setShowDetailsValidation(true);
      if (!isDetailsStepComplete(details)) {
        return;
      }
      setShowDetailsValidation(false);
    }
    setStep((current) => Math.min(current + 1, STEPS.length - 1));
  };

  const onBack = () => {
    if (isProcessing) {
      setIsProcessing(false);
      return;
    }
    setStep((current) => Math.max(current - 1, 0));
  };

  // Walks the steps, skipping any already ticked. The project id lives in a local rather than
  // state so the second step can use what the first just produced.
  const onCreate = async () => {
    setShortcodeError(undefined);
    setIsProcessing(true);

    let projectId = carrierProjectId;
    let completed = run.completed;

    for (const definition of PROCESSING) {
      if (completed.includes(definition.id)) {
        continue;
      }
      setRun({ completed, running: definition.id });

      try {
        if (definition.id === 'project') {
          const project = await createProject.mutateAsync({
            dto: toCreateProjectDto(details, parentProjectId, CARRIER_PROJECT_TYPE)
          });
          projectId = project.id;
          setCarrierProjectId(project.id);
        } else {
          await createOrganisation.mutateAsync({
            dto: toCreateCarrierOrganisationDto(projectId as string, servicing, loginMethod)
          });
        }
        completed = [...completed, definition.id];
      } catch (e) {
        const restError = e as RestError;
        // A taken shortcode is the user's to fix, so it goes back to the field rather than
        // sitting on a screen whose only offer is to try the same thing again.
        if (definition.id === 'project' && restError.statusCode === 409) {
          setRun(NOT_STARTED);
          setIsProcessing(false);
          setShortcodeError('That shortcode is already in use');
          setShowDetailsValidation(true);
          setStep(0);
          return;
        }
        setRun({ completed, failed: { id: definition.id, error: parseErrorMessage(restError.message) || 'Failed.' } });
        return;
      }
    }

    setRun({ completed });
    queryClient.invalidateQueries(QueryKeys.SETTINGS);
    queryClient.invalidateQueries(QueryKeys.PROJECTS);
    onSuccess(projectId as string, details.name.trim());
  };

  // Step 1 validates on Next so it can say what is missing; step 2 just stays disabled.
  const canAdvance = step === 0 ? true : isNetworksStepComplete(servicing);

  return (
    <Modal title="Add Carrier" open divider maxWidth="sm" onClose={isSubmitting ? undefined : onCancel}>
      <DialogContent style={{ padding: '16px 24px' }}>
        <Stepper activeStep={isProcessing ? STEPS.length : step} style={{ padding: '0 0 16px' }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {isProcessing && <ProcessingStep steps={viewSteps(PROCESSING, run)} isRepairing={hasPartialProgress(run)} />}

        {!isProcessing && step === 0 && (
          <DetailsStep
            details={details}
            onChange={(next) => {
              // Editing the shortcode makes the conflict message stale.
              setShortcodeError(undefined);
              setDetails(next);
            }}
            readOnly={detailsLocked}
            shortcodeError={shortcodeError}
            showValidation={showDetailsValidation}
          />
        )}
        {!isProcessing && step === 0 && (
          <Box
            sx={{
              marginTop: 2
            }}
          >
            <Link component="button" type="button" variant="body2" onClick={() => setShowAdvanced((open) => !open)}>
              {showAdvanced ? 'Hide advanced' : 'Advanced'}
            </Link>
            {showAdvanced && (
              <Box
                sx={{
                  marginTop: 2
                }}
              >
                <FormControl variant="outlined" size="small" fullWidth disabled={detailsLocked}>
                  <InputLabel>Couriers sign in with</InputLabel>
                  <Select
                    value={loginMethod}
                    label="Couriers sign in with"
                    onChange={(event) => setLoginMethod(event.target.value as CourierLoginMethod)}
                  >
                    {LOGIN_METHOD_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="textSecondary">
                  Every carrier under this organisation inherits this, and it decides which login the add-courier form
                  opens on.
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {!isProcessing && step === 1 && (
          <NetworksStep
            networks={networks.data ?? []}
            isLoading={networks.isLoading}
            loadError={
              networks.isError
                ? parseErrorMessage(networks.error?.message) || 'Could not load the list of networks.'
                : undefined
            }
            servicing={servicing}
            onChange={setServicing}
            disabled={isSubmitting}
          />
        )}
        {!isProcessing && step === 2 && (
          <ReviewStep
            details={details}
            servicing={servicing}
            networks={networks.data ?? []}
            parentProjectName={parentProject.data?.name}
            loginMethod={loginMethod}
            retryingProjectId={carrierProjectId}
          />
        )}
      </DialogContent>

      <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
        <TextButton color="primary" variant="outlined" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </TextButton>
        {(step > 0 || isProcessing) && (
          <TextButton color="primary" variant="outlined" onClick={onBack} disabled={isSubmitting}>
            Back
          </TextButton>
        )}
        {isProcessing ? (
          <TextButton
            color="primary"
            variant="contained"
            onClick={onCreate}
            isLoading={isSubmitting}
            disabled={isSubmitting || isComplete(PROCESSING, run)}
          >
            Try again
          </TextButton>
        ) : step < STEPS.length - 1 ? (
          <TextButton color="primary" variant="contained" onClick={onNext} disabled={!canAdvance || isSubmitting}>
            Next
          </TextButton>
        ) : (
          <TextButton
            color="primary"
            variant="contained"
            onClick={onCreate}
            disabled={!isNetworksStepComplete(servicing)}
          >
            Create
          </TextButton>
        )}
      </DialogActions>
    </Modal>
  );
}
